/**
 * Registration step. Built to `date-of-birth-at-signup/dob-signup-anchor.html`:
 * Email → Date of birth (three inputs) → Username → collapsed "Have a referral
 * code?" → terms → Create Account.
 *
 * The date replaces the "I confirm I am 18 or older" checkbox this form used to
 * carry, which gated submit and stored nothing. Under 18 is refused here and on
 * the server; no account is created. The username sits after the date because
 * the date can refuse the account outright, and asking someone to pick a name
 * first spends their effort on an account we were never going to create.
 *
 * Referral is never announced. A link code applies silently from `?ref=`; the
 * toggle exists only for someone told a code verbally, and is not rendered at
 * all when the URL already carried one.
 */
import {FormEvent, ReactElement, useState} from "react";
import {DateOfBirthInputs, MINIMUM_AGE, ParsedDob} from "@/components/login/DateOfBirthInputs";
import {UsernameField} from "@/components/login/UsernameField";
import {UsernameCheckState} from "@/hooks/useGetUsernameAvailability";

interface Props {
    email: string;
    onSubmit: (referralCode?: string, dateOfBirth?: number, nickname?: string) => void;
    initialReferralCode?: string;
    disableReferralField?: boolean;
    /** Told at sign-up, not discovered after: on a VPN nothing earns. Caller reads the session's network status. */
    vpnDetected?: boolean;
}

function LoginRegistration(props: Props): ReactElement {
    const [confirmedTerms, setConfirmedTerms] = useState(false);
    const [referralCode, setReferralCode] = useState<string>(props.initialReferralCode || "");
    const [referralOpen, setReferralOpen] = useState<boolean>(Boolean(props.initialReferralCode));
    const [dob, setDob] = useState<ParsedDob | null>(null);
    const [username, setUsername] = useState("");
    const [usernameState, setUsernameState] = useState<UsernameCheckState>({status: "idle"});

    const dobError = !dob
        ? null
        : !dob.plausible
            ? "That date doesn't look right. Check it and try again."
            : dob.age < MINIMUM_AGE
                ? `You must be ${MINIMUM_AGE} or older to use DevourPlay.`
                : null;
    const dobOk = Boolean(dob && dob.plausible && dob.age >= MINIMUM_AGE);
    const underAge = Boolean(dob && dob.plausible && dob.age < MINIMUM_AGE);
    const usernameOk = usernameState.status === "available";
    const canSubmit = confirmedTerms && dobOk && usernameOk;

    function submitSignUp(e: FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        if (!canSubmit || !dob) {
            return;
        }
        props.onSubmit(referralCode || undefined, dob.millis, username.trim());
    }

    return (
        <form onSubmit={submitSignUp}>
            <div className="login-page_content_email-container">
                <label htmlFor="registration-email">Email</label>
                <input
                    id="registration-email"
                    type="email"
                    required
                    placeholder="Enter your email address"
                    value={props.email}
                    disabled={true}
                />
            </div>

            <div className="login-page_content_dob-container">
                <label htmlFor="registration-dob-month">Date of birth</label>
                <DateOfBirthInputs
                    idPrefix="registration-dob"
                    className="login-page_content_dob-grid"
                    onChange={setDob}
                    invalid={Boolean(dobError)}
                />
                <span
                    id="registration-dob-help"
                    className={dobError ? "login-page_content_field-error" : "login-page_content_field-hint"}
                    role={dobError ? "alert" : undefined}
                >
                    {dobError ?? `You must be ${MINIMUM_AGE} or older to use DevourPlay.`}
                </span>
            </div>

            {/* Dimmed, not hidden, when the date already refused the account: the form's shape does not change under them. */}
            <UsernameField
                id="registration-username"
                className="login-page_content_username-container"
                value={username}
                onChange={setUsername}
                onStateChange={setUsernameState}
                disabled={underAge}
                hint="This is how you appear on leaderboards, in guilds and in competitions."
            />

            {!props.disableReferralField && !props.initialReferralCode &&
                <div className="login-page_content_referral-container">
                    {referralOpen
                        ? <>
                            <label htmlFor="registration-referral">Referral code</label>
                            <input
                                id="registration-referral"
                                placeholder="Enter code"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value)}
                            />
                        </>
                        : <button type="button" className="login-page_content_referral-toggle" onClick={() => setReferralOpen(true)}>
                            Have a referral code?
                        </button>}
                </div>}

            {props.vpnDetected &&
                <p className="login-page_content_vpn-notice" role="status">
                    You&rsquo;re on a VPN. You can browse and watch, but Quests, Competitions and Challenges won&rsquo;t count until you turn it off.
                </p>}

            <div className="login-page_content_confirm">
                <label className="login-page_content_confirm_field">
                    <input
                        type="checkbox"
                        className="login-page_content_confirm_field_checkbox"
                        checked={confirmedTerms}
                        onChange={() => setConfirmedTerms(!confirmedTerms)}
                    />
                    <span className="login-page_content_confirm_field_text">
                        I have read, understood, and agree to the{" "}
                        <a href="/terms-of-use">DevourPlay Terms of Use</a> ,{" "}
                        <a href="/privacy-policy">Privacy Policy</a>, and{" "}
                        <a href="/cookie-policy">Cookie Policy</a> .
                    </span>
                </label>
            </div>

            <button
                type="submit"
                className="login-page_content_login-button"
                disabled={!canSubmit}
            >
                Create Account
            </button>
        </form>
    );
}

export default LoginRegistration;
